import { useState } from 'react';
import { Button, Modal, Mono } from '../../components/ui.js';
import { money } from '../../lib/money.js';
import { sizeGrids } from '../purchasing/size-grid.js';
import { LABEL_SIZES } from '../purchasing/label-geometry.js';
import type { LabelSize } from '../purchasing/label-geometry.js';
import { LabelRunFeedback, ScannabilityNotice, useLabelRun } from '../purchasing/labels.js';
import type { useLabelPrinter } from '../purchasing/labels.js';
import {
  buildLabelItems,
  labelableVariants,
  quantitiesFromStock,
  quantitiesNone,
  quantitiesOneEach,
  totalLabels,
} from './label-run.js';
import type { LabelableVariant } from './label-run.js';

/**
 * Printing labels for a style, from Stock (found 2026-08-28, in no batch).
 *
 * **The gap this closes is the whole reason batch 6 was urgent.**
 * `useLabelPrinter` was reachable from the receiving bench and nowhere else, so
 * an item that was never received through us — after the inventory import, most
 * of the catalog — had no route to a label at all. And 90.4% of those rows carry
 * no UPC, so a label is not a convenience for them: it is the only barcode the
 * item is ever going to have.
 *
 * **A modal rather than a screen, and that is the line §7 draws**: this is a
 * grid with one number per size — colors down, sizes across, a grid per
 * length, as the style page and receiving draw it (2026-10-02) — not a
 * workspace. The style is
 * still behind it, which is what you check the sizes against.
 *
 * **Not gated on `catalog.manage`.** Printing a label changes nothing —
 * §5.2 puts reading the catalog behind `inventory.view`, which every role has,
 * and the person who finds a label missing off a jacket is whoever is standing
 * on the shop floor. Making them find a manager to reprint one is how items go
 * back on the rail unlabelled.
 */
export function StyleLabelsDialog({
  productName,
  variants,
  size,
  showPrice,
  showBothPrices,
  printer,
  initialQuantities,
  onClose,
}: {
  productName: string;
  variants: readonly LabelableVariant[];
  size: LabelSize;
  showPrice: boolean;
  showBothPrices: boolean;
  printer: ReturnType<typeof useLabelPrinter>;
  /** Labels already decided — what an adjustment just added (FB-1116). */
  initialQuantities?: Record<string, number> | undefined;
  onClose: () => void;
}) {
  const sellable = labelableVariants(variants);
  const retiredCount = variants.length - sellable.length;

  // Smart default (§2.2-5): one per unit on the shelf is the answer nine times
  // in ten, so the common path is confirmation rather than data entry.
  const [quantities, setQuantities] = useState(() =>
    initialQuantities === undefined
      ? quantitiesFromStock(variants)
      : { ...quantitiesNone(variants), ...initialQuantities },
  );

  const items = buildLabelItems(productName, variants, quantities);
  // As a grid, like every other place a style's sizes are counted (2026-10-02).
  const grids = sizeGrids(sellable);
  const prices = sellable.map((variant) => variant.retailCents);
  const low = Math.min(...prices);
  const high = Math.max(...prices);
  const count = totalLabels(quantities);
  const run = useLabelRun(printer, size, onClose);
  // perUnit, because the quantity per size has already been decided here —
  // this is not the bench's "one each or one per unit" question.
  const options = { size, showPrice, showBothPrices, perUnit: true };

  return (
    <Modal
      open
      wide
      title="Print labels"
      description={`${productName} · ${LABEL_SIZES[size].label} stock, from your store settings.`}
      onClose={onClose}
      {...run.actions(count, () => run.print(items, options, count))}
    >
      <LabelRunFeedback
        run={run}
        count={count}
        onBrowser={() => run.throughBrowser(items, options)}
      />
      <div className="style-labels__presets">
        <span className="text-secondary">Print</span>
        <button
          type="button"
          className="chip"
          onClick={() => setQuantities(quantitiesFromStock(variants))}
        >
          One per unit on hand
        </button>
        <button
          type="button"
          className="chip"
          onClick={() => setQuantities(quantitiesOneEach(variants))}
        >
          One per size
        </button>
        <button
          type="button"
          className="chip"
          onClick={() => setQuantities(quantitiesNone(variants))}
        >
          None
        </button>
      </div>

      {sellable.length > 0 ? (
        <p className="text-secondary">
          On the label: {low === high ? money(low) : `${money(low)} to ${money(high)}, by size`}
        </p>
      ) : null}
      {grids.map((grid) => (
        <div className="table-scroll style-labels__table" key={grid.altSize ?? 'one'}>
          <table className="table matrix">
            {grid.label !== null ? (
              <caption className="matrix__caption">{grid.label}</caption>
            ) : null}
            <thead>
              <tr>
                <th />
                {grid.sizes.map((label, index) => (
                  <th key={label ?? `size-${index}`} className="num">
                    {label ?? 'One size'}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.rows.map((row, rowIndex) => (
                <tr key={row.key}>
                  <th scope="row" className="matrix__row-head">
                    {row.label}
                  </th>
                  {row.cells.map((variant, index) =>
                    variant === undefined ? (
                      <td key={index} />
                    ) : (
                      <td key={variant.id} className="matrix__cell">
                        <input
                          className="price-input num"
                          inputMode="numeric"
                          aria-label={`Labels for ${variant.sku}`}
                          value={String(quantities[variant.id] ?? 0)}
                          onChange={(event) => {
                            const typed = Number.parseInt(event.target.value, 10);
                            setQuantities((current) => ({
                              ...current,
                              [variant.id]: Number.isFinite(typed) ? Math.max(typed, 0) : 0,
                            }));
                          }}
                        />
                        <span className="style-labels__onhand">
                          <Mono>{variant.sku}</Mono> · {variant.stock.onHand} on hand
                        </span>
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Bound a list, then say so — a shortened list that does not announce
          itself reads as though it is everything (§2.2-10). */}
      {retiredCount > 0 ? (
        <p className="text-secondary">
          {retiredCount} inactive {retiredCount === 1 ? 'size is' : 'sizes are'} not listed. An
          inactive size is off the register, so its label would scan to something nobody can sell.
        </p>
      ) : null}

      <ScannabilityNotice items={items} size={size} throughAgent={run.route.kind === 'agent'} />
    </Modal>
  );
}
